import { HttpErrorResponse } from '@angular/common/http';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { BulkGrantPermissionResponseDTO, BulkRemovePermissionResponseDTO } from '../../../common/dtos/access-permissions.dto';
import { OwnAccountDTO } from '../../../common/dtos/own-account.dto';
import { UserDetailsDto } from '../../../common/dtos/user-details.dto';
import { AccessTypeSelection } from '../../../common/enums/access-type-selection.enum';
import { UserTypeSelection } from '../../enums/user-type-selection.enum';
import { AccessFlowStepper, OperationResponse } from '../../interfaces/access-flow-stepper.interface';
import { AddUserStepperFlow } from '../../interfaces/add-user-stepper-flow.interface';
import { RemoveUserStepperFlow } from '../../interfaces/remove-user-stepper-flow.interface';
import accountPermissionUtils from '../../utils/account-permission/account-permission.util';
import * as contentUtils from '../../utils/content/content.util';
import { ApiState } from '../state/api.state';
import { GrantRevokePermissionsState } from '../state/grant-revoke-permissions.state';
import { StoreConstant } from '../store.constant';

export const selectAccessFlowStepperData = createSelector(
    createFeatureSelector(StoreConstant.reducers.grantRevokePermissions),
    (state: GrantRevokePermissionsState): AccessFlowStepper => {
        return state?.accessFlowStepper;
    },
);

export const selectAddUserStepperData = createSelector(
    selectAccessFlowStepperData,
    (stepperFlow: AccessFlowStepper): AddUserStepperFlow => {
        return stepperFlow?.addUser;
    },
);

export const selectAddUserStepperSelectedUserType = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlowData: AddUserStepperFlow): UserTypeSelection => {
        return addUserStepperFlowData?.selectedUserType;
    },
);

export const selectAddUserStepperSelectedUser = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlowData: AddUserStepperFlow): UserDetailsDto => {
        return addUserStepperFlowData?.selectedUserType === UserTypeSelection.EXISTING
            ? addUserStepperFlowData?.selectedUser
            : addUserStepperFlowData?.addedUser?.data;
    },
);

export const selectAddUserStepperAddedUser = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlow: AddUserStepperFlow): ApiState<UserDetailsDto> => {
        return addUserStepperFlow?.addedUser;
    },
);

export const selectAddUserStepperSelectedUserName = createSelector(
    selectAddUserStepperSelectedUser,
    (selectedUser: UserDetailsDto): string =>
        selectedUser ? accountPermissionUtils.constructUserName(selectedUser) : '',
);

export const selectAddUserStepperSelectedUserFirstName = createSelector(
    selectAddUserStepperSelectedUser,
    (selectedUser: UserDetailsDto): string => selectedUser?.firstName || '',
);

export const selectAddUserStepperSelectedAccessLevel = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlowData: AddUserStepperFlow): AccessTypeSelection | null => {
        return addUserStepperFlowData?.selectedAccess;
    },
);

export const selectAddUserStepperSelectedAccessLevelLabel = createSelector(
    selectAddUserStepperSelectedAccessLevel,
    (selectedAccessLevel: AccessTypeSelection | null): string =>
        selectedAccessLevel
            ? contentUtils.getAccessLevelLabelById(
                  selectedAccessLevel,
                  contentUtils.ContentUtil.content,
              )
            : '',
);

export const getAddedUserLoadingState = createSelector(
    selectAccessFlowStepperData,
    (stepperFlow: AccessFlowStepper): boolean => {
        return stepperFlow?.addUser?.addedUser?.isLoading;
    },
);

export const selectAddUserStepperSelectedAccess = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlowData: AddUserStepperFlow): AccessTypeSelection => {
        return addUserStepperFlowData?.selectedAccess;
    },
);

export const selectAddUserStepperSelectedAccounts = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlowData: AddUserStepperFlow): OwnAccountDTO[] => {
        return addUserStepperFlowData?.selectedAccounts || [];
    },
);

export const selectSearchPersonAttemptCounter = createSelector(
    selectAddUserStepperData,
    (addUserStepperFlowData: AddUserStepperFlow): number => {
        return addUserStepperFlowData?.searchPersonAttemptCounter;
    },
);

export const selectGrantRevokePermissionFlowApiErrorState = createSelector(
    createFeatureSelector(StoreConstant.reducers.grantRevokePermissions),
    (state: GrantRevokePermissionsState): HttpErrorResponse | null => {
        return state.accessFlowStepper?.addUser?.addedUser?.error;
    },
);

export const selectGrantRevokePermissionHttpErrorState = createSelector(
    createFeatureSelector(StoreConstant.reducers.grantRevokePermissions),
    (state: GrantRevokePermissionsState): HttpErrorResponse | null => {
        return state.accessFlowStepper?.error;
    },
);

export const selectOperationResponse = createSelector(
    selectAccessFlowStepperData,
    (stepperFlow: AccessFlowStepper): OperationResponse => {
        return stepperFlow?.operationResponse;
    },
);

export const selectGafPartialSuccessAccessLevel = createSelector(
    selectAddUserStepperSelectedAccessLevel,
    (accessLevel: AccessTypeSelection | null): AccessTypeSelection | null => accessLevel,
);

export const selectGafPartialSuccessAccessLevelLabel = createSelector(
    selectAddUserStepperSelectedAccessLevelLabel,
    (label: string): string => label,
);

export const selectGafPartialSuccessAccountNames = createSelector(
    selectOperationResponse,
    selectAddUserStepperSelectedAccounts,
    (operationResponse: BulkGrantPermissionResponseDTO | null, selectedAccounts: OwnAccountDTO[]): string[] => {
        if (!operationResponse?.results) {
            return [];
        }
        return operationResponse.results
            .filter((r) => !!r.confirmationNumber)
            .map((r) => selectedAccounts.find((a) => a.accountId == r.accountId)?.accountName ?? r.accountId);
    },
);

export const selectUniquePin = createSelector(
    selectAccessFlowStepperData,
    (stepperFlow: AccessFlowStepper): string => {
        return stepperFlow?.uniquePin;
    },
);

export const selectSuccessfulAccountsCount = createSelector(
    selectOperationResponse,
    (operationResponse: OperationResponse): number | null => {
        if (!operationResponse) {
            return null;
        }
        if ('succeededAccounts' in operationResponse) {
            return operationResponse.succeededAccounts?.length;
        }
        if ('results' in operationResponse) {
            return operationResponse.results?.filter((r) => !!r.confirmationNumber).length;
        }
        return 0;
    },
);

export const selectLoadingState = createSelector(
    selectAccessFlowStepperData,
    (stepperFlow: AccessFlowStepper): boolean => {
        return stepperFlow?.isLoading;
    },
);

export const selectRemoveUserStepperData = createSelector(
    selectAccessFlowStepperData,
    (stepperFlow: AccessFlowStepper): RemoveUserStepperFlow => {
        return stepperFlow?.removeUser;
    },
);

export const selectRemoveUserStepperSelectedUser = createSelector(
    selectRemoveUserStepperData,
    (removeUserStepperFlowData: RemoveUserStepperFlow): UserDetailsDto => {
        return removeUserStepperFlowData?.selectedUser;
    },
);

export const selectRemoveUserStepperSelectedAccounts = createSelector(
    selectRemoveUserStepperData,
    (removeUserStepperFlowData: RemoveUserStepperFlow): Array<OwnAccountDTO> => {
        return removeUserStepperFlowData?.selectedAccounts;
    },
);

export const selectRemoveUserStepperSelectedUserName = createSelector(
    selectRemoveUserStepperSelectedUser,
    (selectedUser: UserDetailsDto): string =>
        selectedUser ? accountPermissionUtils.constructUserName(selectedUser) : '',
);

export const selectRemoveUserStepperSelectedAccessLevelLabel = createSelector(
    selectRemoveUserStepperSelectedUser,
    selectRemoveUserStepperSelectedAccounts,
    (selectedUser: UserDetailsDto, selectedAccounts: Array<OwnAccountDTO>): string => {
        if (!selectedUser || !selectedAccounts?.length) return '';
        const allGrantees = selectedAccounts.flatMap((a) => a.grantees ?? []);
        const accessLevel = accountPermissionUtils.getAccessLevel(allGrantees, selectedUser.clientPoid, () => {});
        return accessLevel ? contentUtils.getAccessLevelLabelById(accessLevel, contentUtils.ContentUtil.content) : '';
    },
);

export const selectRafPartialSuccessAccessLevelLabel = createSelector(
    selectRemoveUserStepperSelectedAccessLevelLabel,
    (label: string): string => label,
);

export const selectRafPartialSuccessAccountNames = createSelector(
    selectOperationResponse,
    selectRemoveUserStepperSelectedAccounts,
    (operationResponse: BulkRemovePermissionResponseDTO | null, selectedAccounts: OwnAccountDTO[]): string[] => {
        if (!(operationResponse as BulkRemovePermissionResponseDTO)?.results) {
            return [];
        }
        return (operationResponse as BulkRemovePermissionResponseDTO).results
            .filter((r) => !!r.confirmationNumber)
            .map((r) => selectedAccounts?.find((a) => a.accountId === r.accountId)?.accountName ?? r.accountId);
    },
);
